import React, { useEffect } from "react";
import { Helmet } from "react-helmet";

const TermsOfServicePage = () => {
  useEffect(() => {
    document.title = "Terms of Service | Digital Media Ninja";
  }, []);

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="terms-of-service-page">
      <Helmet>
        <title>Terms of Service | Digital Media Ninja</title>
        <meta name="description" content="Digital Media Ninja terms of service. Read our terms and conditions for using our digital marketing services." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://digitalmedianinja.com/terms-of-service" />
      </Helmet>

      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-white mb-8">Terms of Service</h1>
          <p className="text-gray-400 mb-8">Last Updated: February 4, 2026</p>

          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                By accessing or using the Digital Media Ninja website and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Services</h2>
              <p className="text-gray-300 leading-relaxed">
                Digital Media Ninja provides digital marketing services including but not limited to search engine optimization (SEO), pay-per-click advertising (PPC), social media marketing, web design, and related consulting services. Service details, deliverables, and pricing are specified in individual service agreements.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Client Responsibilities</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                As a client, you agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-300 space-y-2">
                <li>Provide accurate and complete information necessary for services</li>
                <li>Maintain confidentiality of account credentials</li>
                <li>Promptly respond to communications and approve deliverables</li>
                <li>Pay fees according to agreed payment terms</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Intellectual Property</h2>
              <p className="text-gray-300 leading-relaxed">
                Upon full payment, you own the deliverables created specifically for your project (excluding pre-existing materials and templates). Digital Media Ninja retains ownership of proprietary processes, methodologies, and tools used in providing services. We reserve the right to display completed work in our portfolio unless otherwise agreed in writing.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Payment Terms</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Payment terms are specified in individual service agreements. General terms include:
              </p>
              <ul className="list-disc pl-6 text-gray-300 space-y-2">
                <li>Monthly retainer services are billed in advance</li>
                <li>Project-based work requires a deposit before commencement</li>
                <li>Payment is due within 30 days of invoice unless otherwise specified</li>
                <li>Late payments may incur a 1.5% monthly interest charge</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Results Disclaimer</h2>
              <p className="text-gray-300 leading-relaxed">
                While we strive for the best results, Digital Media Ninja cannot guarantee specific outcomes such as search rankings, traffic levels, or conversion rates. Results depend on numerous factors including market conditions, competition, and algorithm changes. We commit to using industry best practices and providing transparent reporting.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Limitation of Liability</h2>
              <p className="text-gray-300 leading-relaxed">
                To the maximum extent permitted by law, Digital Media Ninja shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities. Our total liability shall not exceed the amount paid for services in the 12 months preceding the claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Termination</h2>
              <p className="text-gray-300 leading-relaxed">
                Either party may terminate services with 30 days written notice. Upon termination, you are responsible for payment of all services rendered through the termination date. We will provide reasonable assistance in transitioning services to another provider.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Confidentiality</h2>
              <p className="text-gray-300 leading-relaxed">
                Both parties agree to maintain confidentiality of proprietary information shared during the engagement. This obligation survives termination of services. Confidential information does not include publicly available information or information independently developed.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Governing Law</h2>
              <p className="text-gray-300 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of Texas, without regard to conflict of law principles. Any disputes shall be resolved in the courts of Dallas County, Texas.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Changes to Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be posted on this page with an updated effective date. Continued use of our services after changes constitutes acceptance of the modified terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Contact</h2>
              <p className="text-gray-300 leading-relaxed">
                For questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10">
                <p className="text-white">Digital Media Ninja</p>
                <p className="text-gray-400">3333 Harry Hines Blvd, Dallas, TX 75201</p>
                <p className="text-gray-400">Email: info@digitalmedianinja.com</p>
                <p className="text-gray-400">Phone: (432) 934-9470</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
